/**
 * Brand and phishing-pattern blocklists.
 *
 * Curated rather than exhaustive on purpose. Enumerating every trademark is
 * impossible and the USPTO has no realtime API suited to it. The goal is
 * narrower: stop the impersonation that actually damages a multi-tenant
 * domain's reputation -- payment providers, banks, crypto wallets, and the
 * consumer brands whose users get phished.
 *
 * Entries are stored pre-skeletonized, so matching is a plain comparison.
 */
import { skeleton } from './normalize.js'

/**
 * Distinctive marks: coined words with no ordinary-language meaning.
 *
 * Because nobody names a legitimate startup `paypalpro`, these are safe to
 * match on containment and on fuzzy distance.
 */
const STRICT_BRANDS = [
  // payments and banking
  'paypal', 'venmo', 'cashapp', 'zelle', 'revolut', 'mastercard',
  'americanexpress', 'wellsfargo', 'citibank', 'scotiabank', 'interac',
  // crypto
  'coinbase', 'binance', 'metamask', 'trezor', 'bitfinex', 'uniswap',
  'trustwallet', 'blockchain',
  // big tech and consumer accounts
  'icloud', 'gmail', 'microsoft', 'office365', 'netflix', 'spotify',
  'instagram', 'whatsapp', 'linkedin', 'tiktok', 'snapchat', 'youtube',
  'github', 'gitlab', 'dropbox', 'salesforce', 'docusign', 'shopify',
  'squarespace', 'godaddy', 'playstation', 'nintendo', 'roblox', 'twitch',
  // shipping and government
  'fedex', 'purolator', 'canadapost', 'servicecanada',
  // consumer brands
  'nike', 'adidas', 'gucci', 'rolex', 'disney',
] as const

/**
 * Marks that are also ordinary words, or too short to match safely.
 *
 * `crypto`, `apple` and `visa` are real vocabulary -- "Crypto Analytics",
 * "Apple Orchard Co" and "Visa Consulting" are all plausible, legitimate
 * names. Matching these on containment would block honest founders constantly,
 * and a false positive on a paid product is far more expensive than a missed
 * marginal case. So these only ever fire when paired with a phishing keyword:
 * `apple` passes, `apple-verify` does not.
 */
const CONTEXTUAL_BRANDS = [
  'visa', 'amex', 'discover', 'chase', 'hsbc', 'barclays', 'santander',
  'wise', 'stripe', 'ledger', 'phantom', 'exodus', 'kraken', 'opensea',
  'crypto', 'bitcoin', 'ethereum', 'solana',
  'apple', 'google', 'amazon', 'aws', 'meta', 'facebook', 'twitter',
  'outlook', 'adobe', 'oracle', 'slack', 'zoom', 'wix', 'steam', 'discord',
  'xbox', 'tesla', 'walmart', 'costco', 'tangerine',
  'rbc', 'bmo', 'cibc', 'ups', 'dhl', 'usps', 'irs', 'cra', 'hmrc',
] as const

/**
 * Words that turn a brand mention into a credential-harvesting pattern.
 *
 * `acme-support` is fine; `paypal-support` is not. The combination carries the
 * signal, which is why these are matched against brands rather than alone.
 */
const PHISHING_KEYWORDS = [
  'verify', 'verification', 'validate', 'confirm', 'secure', 'security',
  'login', 'signin', 'account', 'auth', 'authenticate', 'password',
  'support', 'helpdesk', 'billing', 'invoice', 'payment', 'refund',
  'wallet', 'restore', 'recover', 'recovery', 'unlock', 'suspended', 'locked',
  'reactivate', 'claim', 'airdrop', 'giveaway', 'bonus', 'reward',
  'kyc', 'otp', 'seed', 'seedphrase', 'privatekey', 'official',
] as const

/**
 * Terms blocked alone, with no brand required.
 *
 * No legitimate landing page needs `seedphrase` in its URL; every use is an
 * attempt to harvest wallet credentials.
 */
const STANDALONE_HIGH_RISK = [
  'seedphrase', 'privatekey', 'recoveryphrase', 'walletconnect',
  'walletrestore', 'restorewallet', 'airdropclaim', 'claimairdrop',
  'freecrypto', 'doublecrypto', 'cryptogiveaway', 'verifywallet',
  'syncwallet', 'validatewallet', 'migratewallet', 'connectwallet',
] as const

/** Below this length, containment matching is too noisy to be useful. */
export const MIN_CONTAINMENT_LENGTH = 5

export const STRICT: readonly string[] = STRICT_BRANDS.map(skeleton)
export const CONTEXTUAL: readonly string[] = CONTEXTUAL_BRANDS.map(skeleton)
export const KEYWORDS: readonly string[] = PHISHING_KEYWORDS.map(skeleton)
export const STANDALONE: readonly string[] = STANDALONE_HIGH_RISK.map(skeleton)

/** All brands, either tier -- used for the combination rule. */
export const ALL_BRANDS: readonly string[] = [...STRICT, ...CONTEXTUAL]

/** Skeleton -> original spelling, so a block can be logged readably. */
export const BRAND_DISPLAY: ReadonlyMap<string, string> = new Map(
  [...STRICT_BRANDS, ...CONTEXTUAL_BRANDS].map((b) => [skeleton(b), b]),
)
